import { MediaFileTypeGroupEnum, type IMediaFile } from "@/shared/types";
import Tooltip from "@components/ui/tooltip";
import { makeImageElementFromMediaFile } from "@editor/modules/media-lib/modules/images-panel/utils";
import { makeVideoElementFromMediaFile } from "@editor/modules/media-lib/modules/videos-panel/utils";
import {
  MediaLibPresetEnum,
  type TElement,
} from "@editor/modules/media-lib/types";
import { cn } from "@lib/cn";
import { memo, useMemo, type ReactNode } from "react";
import { isFontMimeType } from "@/shared/utils/font-mime-type";
import { useMediaLibFilesCtx } from "../media-lib-files.ctx";
import { MediaLibFileActions } from "./media-lib-file-actions";
import { MediaLibFileCheckbox } from "./media-lib-file-checkbox";
import { MediaLibFileFont } from "./media-lib-file-font";
import { MediaLibFileImage } from "./media-lib-file-image";
import { MediaLibFileVideo } from "./media-lib-file-video";

interface IProps {
  fileData: IMediaFile;
}

export const MediaLibFile = ({ fileData }: IProps) => {
  const {
    selectedItems,
    onSelect,
    isMultiSelect,
    view,
    mediaPreset,
    preset: ctxPreset,
    source,
  } = useMediaLibFilesCtx();
  const preset = mediaPreset ?? ctxPreset;
  const shouldHideActions = selectedItems && selectedItems.length > 0;
  const mediaType = fileData.typeGroup;

  const isImage = mediaType === MediaFileTypeGroupEnum.Image;
  const isVideo = mediaType === MediaFileTypeGroupEnum.Video;
  const isImageOnly = preset === MediaLibPresetEnum.OnlyImages && isImage;
  const isVideoOnly = preset === MediaLibPresetEnum.OnlyVideos && isVideo;
  const isAll = preset === MediaLibPresetEnum.All;

  const isSelected = useMemo(() => {
    return !!selectedItems?.find((item) => item.id === fileData._id);
  }, [selectedItems, fileData]);

  const [content, element] = useMemo(() => {
    let content = null;
    let element: TElement | null = null;

    if (fileData.type.startsWith("image/")) {
      content = (
        <MediaLibFileImage fileData={fileData} isSelected={isSelected} />
      );
      element = makeImageElementFromMediaFile({
        mediaFile: fileData,
      });
    }

    if (fileData.type.startsWith("video/")) {
      content = (
        <MediaLibFileVideo fileData={fileData} isSelected={isSelected} />
      );
      element = makeVideoElementFromMediaFile({
        mediaFile: fileData,
      });
    }

    if (isFontMimeType(fileData.type)) {
      content = (
        <MediaLibFileFont isSelected={isSelected} fileName={fileData.title} />
      );
    }

    return [content, element];
  }, [fileData, isSelected]);

  const hasMultiSelect = useMemo(() => {
    return (
      (isMultiSelect &&
        !fileData.archived &&
        (isAll || isImageOnly || isVideoOnly)) ||
      source === "settings"
    );
  }, [isMultiSelect, fileData, isAll, isImageOnly, isVideoOnly, source]);

  const tooltipType = useMemo(() => {
    if (isAll) return "all";
    if (isImage && !isImageOnly && view === "dialog") return "videos";
    if (isVideo && !isVideoOnly && view === "dialog") return "images";
    return "all";
  }, [isAll, isImageOnly, isVideoOnly, isImage, isVideo, view]);

  return (
    <LockTooltip type={tooltipType}>
      <div className="tw-group tw-relative">
        {!shouldHideActions && <MediaLibFileActions fileData={fileData} />}
        {hasMultiSelect && (
          <MediaLibFileCheckbox
            isSelected={isSelected}
            onClick={() => {
              onSelect?.(fileData);
            }}
          />
        )}

        <div
          onClick={() => {
            if (view === "dialog") {
              onSelect?.(fileData);
            } else if (shouldHideActions && hasMultiSelect) {
              onSelect?.(fileData);
            }
          }}
          className={cn(
            tooltipType === "videos" && "[&_img]:!tw-cursor-not-allowed",
            tooltipType === "images" && "[&_video]:!tw-cursor-not-allowed",
          )}
        >
          <Wrapper element={element} disableWrapper={!!shouldHideActions}>
            {content}
          </Wrapper>
        </div>
      </div>
    </LockTooltip>
  );
};

const Wrapper = memo(
  ({
    children,
    element,
    disableWrapper,
  }: {
    children: ReactNode;
    element: TElement | null;
    disableWrapper?: boolean;
  }) => {
    const { elementWrapper } = useMediaLibFilesCtx();
    if (!elementWrapper || !element) return <>{children}</>;
    const ElementWrapper = elementWrapper;

    return (
      <ElementWrapper element={element} disabled={disableWrapper}>
        {children}
      </ElementWrapper>
    );
  },
);

const LockTooltip = ({
  children,
  type,
}: {
  children: ReactNode;
  type: "images" | "videos" | "all";
}) => {
  if (type === "all") return <>{children}</>;

  return (
    <Tooltip>
      <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
      <Tooltip.Content className="tw-z-[99999]">
        <Tooltip.Text>You can only add {type}</Tooltip.Text>
      </Tooltip.Content>
    </Tooltip>
  );
};
